import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, CircleDashed, Loader2, ServerCrash, XCircle } from 'lucide-react';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router';
import { type BrandInput, Button, Dialog, DialogContent, notify } from '@athlete/ui';
import { SERVICE_RECHECK_MS, useBootstrap } from '@modules/base/desktop/api/hooks';
import type { Bootstrap } from '@modules/base/desktop/api/types';
import { isApiError } from '@/api';
import { type Formatters, makeFormatters } from './format';
import { useProto } from './store';
import { useOrgTheme } from './theme';

const BootstrapContext = createContext<Bootstrap | null>(null);
const FormatContext = createContext<Formatters | null>(null);

export function useBootstrapData(): Bootstrap {
  const b = useContext(BootstrapContext);
  if (!b) throw new Error('useBootstrapData must be used inside <BootstrapGate>');
  return b;
}

export const useOrganization = () => useBootstrapData().organization;
export const useCurrentUser = () => useBootstrapData().user;
export const useEnabledModules = () => useBootstrapData().modules;

export function useFormat(): Formatters {
  const f = useContext(FormatContext);
  if (!f) throw new Error('useFormat must be used inside <BootstrapGate>');
  return f;
}

/** Permission checks for *presentation only*. The server enforces every permission independently. */
export function usePermissions() {
  const { permissions } = useBootstrapData();
  return useMemo(() => {
    const set: ReadonlySet<string> = new Set(permissions);
    return {
      set,
      can: (p: string) => set.has(p),
      canAny: (list: string[]) => list.some((p) => set.has(p)),
    };
  }, [permissions]);
}

/* ------------------------------------------------------------------- Gate */

export function BootstrapGate({ children }: { children: ReactNode }) {
  const query = useBootstrap();
  const qc = useQueryClient();
  const role = useProto((s) => s.role);
  const serviceDown = useProto((s) => s.serviceDown);

  // Switching preview role (or service state) must never show data fetched for another role.
  // Only reset on an actual change: resetting on mount would discard the in-flight bootstrap request.
  const previous = useRef({ role, serviceDown });
  useEffect(() => {
    if (previous.current.role !== role || previous.current.serviceDown !== serviceDown) {
      previous.current = { role, serviceDown };
      void qc.resetQueries();
    }
  }, [role, serviceDown, qc]);

  if (query.data) return <ReadyGate data={query.data}>{children}</ReadyGate>;
  if (query.isError) {
    // Nobody is signed in (or the session ended): the sign-in page, not the "service unavailable" screen.
    if (isApiError(query.error) && query.error.status === 401) return <Navigate to="/login" replace />;
    return <ServiceRecovery onRetry={() => query.refetch()} retrying={query.isFetching} />;
  }
  return <BootSplash attempt={query.failureCount} />;
}

function ReadyGate({ data, children }: { data: Bootstrap; children: ReactNode }) {
  useOrgTheme(data.organization.brand as BrandInput | null);
  const formatters = useMemo(() => makeFormatters(data.organization.regional), [data.organization.regional]);
  return (
    <BootstrapContext.Provider value={data}>
      <FormatContext.Provider value={formatters}>{children}</FormatContext.Provider>
    </BootstrapContext.Provider>
  );
}

/* -------------------------------------------------------- Boot / recovery */

export function BootSplash({ attempt }: { attempt: number }) {
  return (
    <main className="grid min-h-dvh place-items-center bg-bg px-6" aria-busy="true">
      <div role="status" className="flex flex-col items-center text-center">
        <div className="mb-5 grid size-14 place-items-center rounded-2xl bg-brand text-on-brand shadow-md">
          <Loader2 className="size-6 animate-spin" aria-hidden />
        </div>
        <p className="text-md font-semibold text-fg">{attempt > 0 ? 'Backend starting…' : 'Starting Athlete Platform…'}</p>
        <p className="mt-1 max-w-xs text-sm text-muted">
          {attempt > 0 ? 'The local service is still starting up. This usually takes a few seconds after Windows starts.' : 'Checking the local service.'}
        </p>
      </div>
    </main>
  );
}

const CHECKS = [
  { label: 'Backend service', detail: 'Not responding', icon: XCircle, tone: 'text-danger-fg' },
  { label: 'Database service', detail: 'Not checked', icon: CircleDashed, tone: 'text-subtle' },
  { label: 'Storage', detail: 'Not checked', icon: CircleDashed, tone: 'text-subtle' },
] as const;

export function ServiceRecovery({ onRetry, retrying }: { onRetry: () => void; retrying: boolean }) {
  const [diagOpen, setDiagOpen] = useState(false);
  return (
    <main className="grid min-h-dvh place-items-center bg-bg px-6 py-10">
      <div className="w-full max-w-lg rounded-xl border bg-surface p-8 shadow-md">
        <div className="mb-5 grid size-12 place-items-center rounded-xl bg-danger-bg text-danger-fg">
          <ServerCrash className="size-6" aria-hidden />
        </div>
        <h1 className="text-xl font-semibold text-fg">Service unavailable</h1>
        <p className="mt-2 text-base text-muted">
          Athlete Platform could not reach its local service. Your data is stored safely on this computer and has not been changed. This often clears within a minute
          after Windows starts.
        </p>

        <ul className="mt-6 divide-y rounded-lg border" aria-label="Service status">
          {CHECKS.map((c) => {
            const Icon = c.icon;
            return (
              <li key={c.label} className="flex items-center gap-3 px-4 py-3">
                <Icon className={`size-5 ${c.tone}`} aria-hidden />
                <span className="flex-1 text-base font-medium text-fg">{c.label}</span>
                <span className="text-sm text-muted">{c.detail}</span>
              </li>
            );
          })}
        </ul>

        <p role="status" className="mt-4 flex items-center gap-2 text-sm text-muted">
          <Loader2 className={`size-4 shrink-0 ${retrying ? 'animate-spin' : ''}`} aria-hidden />
          {retrying ? 'Checking the service…' : `Checking again automatically every ${SERVICE_RECHECK_MS / 1000} seconds.`}
        </p>

        <div className="mt-6 flex flex-wrap gap-2">
          <Button variant="primary" onClick={onRetry} loading={retrying}>
            Retry
          </Button>
          <Button onClick={() => notify.info('Restart requested', 'Windows may ask for permission to restart the services.')}>Restart services</Button>
          <Button variant="ghost" onClick={() => setDiagOpen(true)}>
            Open diagnostics
          </Button>
        </div>
      </div>

      <Dialog open={diagOpen} onOpenChange={setDiagOpen}>
        <DialogContent
          title="Diagnostics"
          description="Information your support contact may ask for."
          footer={
            <>
              <Button onClick={() => notify.success('Support bundle created', 'Saved to your Desktop as AthletePlatform-Support.zip')}>Create support bundle</Button>
              <Button variant="primary" onClick={() => setDiagOpen(false)}>
                Close
              </Button>
            </>
          }
        >
          <dl className="space-y-3 text-base">
            <div>
              <dt className="text-sm text-muted">Services</dt>
              <dd className="font-medium">AthletePlatformDatabase, AthletePlatformBackend</dd>
            </div>
            <div>
              <dt className="text-sm text-muted">Log folder</dt>
              <dd className="font-mono text-sm">C:\ProgramData\Athlete Platform\logs</dd>
            </div>
            <div>
              <dt className="text-sm text-muted">Backend address</dt>
              <dd className="font-mono text-sm">127.0.0.1:45433</dd>
            </div>
          </dl>
          <p className="mt-4 flex items-start gap-2 text-sm text-muted">
            <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success-fg" aria-hidden />
            The support bundle contains logs and version numbers only. It never includes athlete or clinical information.
          </p>
        </DialogContent>
      </Dialog>
    </main>
  );
}
